/**
 * Plain-language labels for the Livi lesson review screen (Learning Loop 1.3).
 * Values mirror backend/assets/lambda/layers/livisibly_common/src/agent/
 * lesson_proposals.py (statuses, tiers, categories, origins) and
 * lesson_scrub.py (warning rules).
 */
import { TEST_RUN_STALE_MS } from '../../api/lessonreview';

export const TIER_LABELS = {
  customer: 'This firm',
  central: 'Livisibly-wide',
};

export const STATUS_LABELS = {
  PENDING: 'Waiting for review',
  APPROVED_LIVE: 'Live',
  APPROVED_STAGED: 'Staged for release',
  REJECTED: 'Rejected',
  SUPERSEDED: 'Superseded',
};

export const STATUS_VARIANTS = {
  PENDING: 'warning',
  APPROVED_LIVE: 'success',
  APPROVED_STAGED: 'info',
  REJECTED: 'error',
  SUPERSEDED: 'default',
};

export const CATEGORY_LABELS = {
  procedure: 'Procedure',
  normalization: 'Data consistency',
  observation: 'Pattern to check',
  style: 'Presentation',
};

export const KIND_LABELS = {
  new: 'New lesson',
  amend: 'Change to an existing lesson',
};

export const ORIGIN_LABELS = {
  case_session: 'Suggested in a case chat',
  super_admin_authored: 'Written by a super-admin',
  reflect: "Suggested by Livi's review of past work",
};

export const SCRUB_WARNING_LABELS = {
  fact_shape: 'Reads like a fact about one case',
  full_date: 'Mentions a specific date',
  instruction_like: 'Reads like an instruction aimed at the AI',
  business_name_shape: 'Mentions what looks like a business name',
  name_shape: "Mentions what looks like a person's name",
  too_short_for_a_procedure: 'Very short for a procedure',
};

export const SESSION_TYPE_LABELS = {
  CASE_WORK: 'Case chat',
  CASE_INTERVIEW: 'Case interview',
  TOPIC_INVESTIGATION: 'Topic investigation',
  PIPELINE_HIL: 'Document review',
  DISCOVERY_DIG: 'Automatic discovery',
  SYSTEM_WORK: 'Super-admin chat',
};

export const POINTER_ACTION_LABELS = {
  create: 'Created',
  advance: 'New version approved',
  rollback: 'Rolled back',
  retire: 'Retired',
  restore: 'Restored',
};

/** A lesson's tests (Learning Loop 2.2) — mirrors agent/lesson_tests.py (outcomes, verdicts, gate codes). */
export const TEST_OUTCOME_LABELS = {
  helps: 'Helps',
  already_default: 'Already does this',
  does_not_work: "Doesn't work",
  makes_worse: 'Makes it worse',
  not_tested: "Didn't test it",
  error: 'Test error',
  incomplete: 'Ran out of time',
  untestable: "Can't be tested yet",
};

export const TEST_OUTCOME_VARIANTS = {
  helps: 'success',
  already_default: 'info',
  does_not_work: 'error',
  makes_worse: 'error',
  not_tested: 'warning',
  error: 'warning',
  incomplete: 'warning',
  untestable: 'default',
};

export const TEST_OUTCOME_HINTS = {
  helps: 'Livi did it with the lesson and not without — the lesson works and is needed.',
  already_default: 'Livi did it either way — the lesson may not be needed.',
  does_not_work: 'Livi still missed this with the lesson.',
  makes_worse: 'Livi did this without the lesson but not with it — the lesson gets in the way here.',
  not_tested: 'Livi never reached the point where the lesson applies, so this says nothing about the lesson.',
  error: 'The test runs failed — run the tests again.',
  incomplete: 'Time ran out before every run finished — run the tests again.',
  untestable: "Lessons for this kind of chat can't be tested yet.",
};

/** Outcomes that mean the reviewer has something to fix — their evidence opens by default, with next steps. */
export const TEST_FAILING_OUTCOMES = ['does_not_work', 'makes_worse', 'not_tested'];

/** The failing outcomes in a finished report, e.g. { does_not_work: 1 } — empty when nothing needs fixing. */
export function failingOutcomes(report) {
  const counts = {};
  (report?.scenarios || []).forEach((r) => {
    if (TEST_FAILING_OUTCOMES.includes(r.outcome)) counts[r.outcome] = (counts[r.outcome] || 0) + 1;
  });
  return counts;
}

export const TEST_VERDICT_LABELS = {
  passed: 'Tests passed',
  failed: "Tests didn't pass",
  inconclusive: 'Tests inconclusive',
};

export const TEST_VERDICT_VARIANTS = {
  passed: 'success',
  failed: 'error',
  inconclusive: 'warning',
};

/**
 * Where a proposal's tests stand, for the revision on screen:
 * no_scenarios · not_tested · outdated (last run was of another revision) · running · stalled · error · done.
 * The approval gate (agent/lesson_tests.py::gate) reads the same fields.
 */
export function testState(proposal, now = Date.now()) {
  const report = proposal?.eval_report;
  if (!(proposal?.test_spec?.scenarios || []).length) return 'no_scenarios';
  if (!report) return 'not_tested';
  if (Number(report.revision) !== Number(proposal.revision)) return 'outdated';
  if (report.status === 'running') {
    const started = Date.parse(report.requested_at || '');
    return Number.isFinite(started) && now - started > TEST_RUN_STALE_MS ? 'stalled' : 'running';
  }
  if (report.status === 'error') return 'error';
  return 'done';
}

/** A short badge for the queue table: { label, variant }. */
export function testStatusBadge(proposal) {
  const state = testState(proposal);
  if (state === 'done') {
    const verdict = proposal.eval_report.verdict;
    return { label: TEST_VERDICT_LABELS[verdict] || 'Tested', variant: TEST_VERDICT_VARIANTS[verdict] || 'default' };
  }
  return {
    no_scenarios: { label: 'No tests', variant: 'default' },
    not_tested: { label: 'Not tested', variant: 'default' },
    outdated: { label: 'Not tested', variant: 'default' },
    running: { label: 'Testing…', variant: 'info' },
    stalled: { label: 'Tests stalled', variant: 'warning' },
    error: { label: 'Tests failed to run', variant: 'warning' },
  }[state];
}

/**
 * Mirrors the backend approval gate: what stops approving this revision now, or null.
 * `alwaysApply` is the reviewer's choice — a Livisibly-wide lesson must be tested as it will apply.
 */
export function testGateProblem(proposal, alwaysApply) {
  const central = proposal?.target_tier === 'central';
  const state = testState(proposal);
  if (state === 'no_scenarios') return { code: 'LESSON_UNTESTED', message: 'This lesson has no test scenarios yet.' };
  if (state === 'not_tested' || state === 'outdated') {
    return { code: 'LESSON_UNTESTED', message: "This revision hasn't been tested yet." };
  }
  if (state === 'running') return { code: 'LESSON_TEST_RUNNING', message: 'The tests are still running.' };
  if (state === 'stalled') return { code: 'LESSON_TEST_RUNNING', message: 'The tests stalled — run them again.' };
  if (state === 'error') return { code: 'LESSON_TEST_FAILED', message: "The tests couldn't run — run them again." };
  if (proposal.eval_report.verdict !== 'passed') {
    return { code: 'LESSON_TEST_FAILED', message: "The tests didn't pass." };
  }
  if (central && Boolean(proposal.eval_report.tested_as?.mandatory) !== Boolean(alwaysApply)) {
    return { code: 'LESSON_TEST_MISMATCH', message: `It was tested as "${
      proposal.eval_report.tested_as?.mandatory ? 'Always apply' : 'Look up when relevant'}".` };
  }
  return null;
}

export function tierLabel(tier) {
  return TIER_LABELS[tier] || tier || '—';
}

export function statusLabel(status) {
  return STATUS_LABELS[status] || status || '—';
}

export function categoryLabel(category) {
  return CATEGORY_LABELS[category] || category || '—';
}

export function sessionTypeLabel(sessionType) {
  return SESSION_TYPE_LABELS[sessionType] || sessionType || '—';
}

/**
 * Firms, cases and people are shown by the name the review service resolved —
 * never by id (nobody recognizes one). When no name could be found, say so
 * plainly; callers put the id in the element's title for support.
 */
export const UNKNOWN_NAME = {
  firm: 'Unknown firm',
  case: 'Unknown case',
  person: 'Unknown person',
};

export function displayName(name, kind) {
  return name || UNKNOWN_NAME[kind] || '—';
}

export function scrubWarningLabel(rule) {
  return SCRUB_WARNING_LABELS[rule] || rule;
}

/** Unique warning rules from every scrub stage recorded on a proposal. */
export function scrubWarnings(scrub) {
  const rules = new Set();
  Object.values(scrub || {}).forEach((stage) => {
    (stage?.warnings || []).forEach((w) => w?.rule && rules.add(w.rule));
  });
  return [...rules];
}
